const INLINE_TAGS = new Set(['STRONG', 'B', 'EM', 'I', 'U']);
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI']);
// Dropped with everything in them: their text is code or styling, never words for the line.
const DROPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'SVG', 'TITLE', 'HEAD']);

/**
 * The disclaimer's rich text: bold, italic, underline and line breaks, with no attributes. Everything else is unwrapped to its
 * text, so a paste from a document or a hand-edited URL can't put markup, styles or scripts on the page. Prototype chrome only.
 */
export class RichText {
    /** `html` reduced to the allowed tags; parsed in an inert document, so nothing in it runs or loads. */
    public static sanitize(html: string): string {
        const source = new DOMParser().parseFromString(html, 'text/html').body;
        const target = document.createElement('div');
        RichText.copy(source, target);
        while (target.lastChild?.nodeName === 'BR') {
            target.lastChild.remove();
        }

        return target.innerHTML;
    }

    public static fragment(html: string): DocumentFragment {
        const template = document.createElement('template');
        template.innerHTML = RichText.sanitize(html);
        return template.content;
    }

    /** No visible text: only tags, line breaks or spaces. */
    public static isEmpty(html: string): boolean {
        return (RichText.fragment(html).textContent ?? '').trim() === '';
    }

    private static copy(from: Node, to: Node) {
        from.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                to.appendChild(document.createTextNode(node.textContent ?? ''));
            } else if (node instanceof Element && !DROPPED_TAGS.has(node.tagName.toUpperCase())) {
                if (node.tagName === 'BR') {
                    to.appendChild(document.createElement('br'));
                } else if (INLINE_TAGS.has(node.tagName)) {
                    const copy = document.createElement(node.tagName.toLowerCase());
                    RichText.copy(node, copy);
                    to.appendChild(copy);
                } else {
                    // A block (the editor wraps each line in a div) ends its line.
                    if (BLOCK_TAGS.has(node.tagName) && to.lastChild && to.lastChild.nodeName !== 'BR') {
                        to.appendChild(document.createElement('br'));
                    }

                    RichText.copy(node, to);
                }
            }
        });
    }
}
